// Unified "Erfassen" SidePanel — one panel, three tabs (Eintrag / Abwesenheit /
// Meeting). Replaces the previous trio of buttons on the Calendar page and the
// inline Meeting dialog on the Meetings page.
//
// Tab behaviour:
//   • create-mode → user can switch tabs freely; switching with a dirty form
//     shows a small "verworfen" toast.
//   • edit-mode (entry / absence / meeting prop set) → panel auto-jumps to the
//     matching tab and the other tabs are disabled (titled tooltip).
//
// Per-tab forms are headless hooks (`useEntryForm`, `useAbsenceForm`,
// `useMeetingForm`) exposed by their respective Dialog files. We render all
// three at once but only show the active one — this keeps each form's state
// instance-stable so users can swap tabs (in create-mode) and recover their
// last input by switching back. The "verworfen" toast is informational; we
// don't actually clear state on switch because the form instances persist.

import { CalendarOff, Clock, Video } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import type { Absence, Meeting, TimeEntry } from '../lib/api'
import { notify } from '../lib/notify'
import { cn } from '../lib/utils'
import { useAbsenceForm } from './absence-form-dialog'
import { type EntryDraft, useEntryForm } from './entry-form-dialog'
import { useMeetingForm } from './meeting-form-dialog'
import { SidePanel } from './ui/side-panel'

export type ErfassenTab = 'entry' | 'absence' | 'meeting'

interface ErfassenPanelProps {
  open: boolean
  onClose: () => void
  defaultTab?: ErfassenTab
  /** Optional pre-filled day for new entries / absences / meetings. */
  dayContext?: Date
  /** Edit mode — when set, the panel auto-jumps to the matching tab and
   * locks tab switching. */
  entry?: TimeEntry | null
  absence?: Absence | null
  meeting?: Meeting | null
  /** Calendar drag-select pre-fills entry start/end. */
  draft?: EntryDraft | null
  /** Required for meeting form (participant diffing). */
  myId: string
  /** Admin/leader edit-mode: when set, NEW time entries are created for this
   * user instead of the session user. Updates/deletes already target the
   * entry's owner server-side. */
  targetUserId?: string | null
  /** Optional callback after a save — Meetings page uses it to refetch. */
  onSaved?: () => void
}

const TABS: { id: ErfassenTab; label: string; Icon: typeof Clock }[] = [
  { id: 'entry', label: 'Eintrag', Icon: Clock },
  { id: 'absence', label: 'Abwesenheit', Icon: CalendarOff },
  { id: 'meeting', label: 'Meeting', Icon: Video },
]

export function ErfassenPanel({
  open,
  onClose,
  defaultTab = 'entry',
  dayContext,
  entry = null,
  absence = null,
  meeting = null,
  draft = null,
  myId,
  targetUserId,
  onSaved,
}: ErfassenPanelProps) {
  // Edit-mode is "locked" — only the matching tab is selectable.
  const lockedTab: ErfassenTab | null = entry
    ? 'entry'
    : absence
      ? 'absence'
      : meeting
        ? 'meeting'
        : null

  const [active, setActive] = useState<ErfassenTab>(lockedTab ?? defaultTab)

  // Re-sync when the panel opens (defaultTab may have changed, or we may now
  // be in edit-mode after previously being in create-mode).
  useEffect(() => {
    if (!open) return
    setActive(lockedTab ?? defaultTab)
  }, [open, lockedTab, defaultTab])

  const defaultDateIso = useMemo(() => {
    if (!dayContext) return undefined
    return dayContext.toISOString()
  }, [dayContext])

  // Mount all three forms with the same `open` so their state survives tab
  // switches (the reset-effect inside each hook keys off `open`, so we want
  // it to fire once when the panel opens and never on tab switching). The
  // queries inside each hook are independently gated on `open` too — opening
  // the panel does fetch dropdown data for all three tabs at once, but the
  // results are tiny and shared via React Query cache.
  const entryForm = useEntryForm({
    open,
    entry,
    defaultDate: defaultDateIso,
    draft,
    onClose,
    forUserId: targetUserId,
  })
  const absenceForm = useAbsenceForm({
    open,
    absence,
    defaultDate: defaultDateIso,
    onClose,
  })
  const meetingForm = useMeetingForm({
    open,
    myId,
    meeting,
    defaultDate: defaultDateIso,
    onClose,
    onSaved,
  })

  // Track which form was active last so we can detect "you had unsaved input
  // and switched away". The form objects get new identities on every render
  // (each keystroke), so we keep their CURRENT snapshot in a ref and only
  // re-run this effect on `active` changes — otherwise the effect would
  // schedule on every keystroke even though nothing meaningful changed.
  const lastActiveRef = useRef<ErfassenTab>(active)
  const formsRef = useRef({ entry: entryForm, absence: absenceForm, meeting: meetingForm })
  formsRef.current = { entry: entryForm, absence: absenceForm, meeting: meetingForm }
  useEffect(() => {
    const prev = lastActiveRef.current
    if (prev === active) return
    const prevForm = formsRef.current[prev]
    if (prevForm.isDirty) {
      notify.info(`${tabLabel(prev)}-Daten bleiben im Tab erhalten`)
    }
    lastActiveRef.current = active
  }, [active])

  const current = active === 'entry' ? entryForm : active === 'absence' ? absenceForm : meetingForm

  const onTabClick = (tab: ErfassenTab) => {
    if (lockedTab && tab !== lockedTab) return
    setActive(tab)
  }

  return (
    <SidePanel
      open={open}
      onClose={onClose}
      title={current.title}
      description={current.description}
      width="sm:w-[480px] lg:w-[560px]"
      footer={current.footer}
      // Tab-Leiste im fixierten Sheet-Header: scrollt nicht mehr mit dem
      // Formular aus dem Sichtbereich und bleibt immer antippbar (D5).
      headerExtra={<TabBar active={active} lockedTab={lockedTab} onSelect={onTabClick} />}
    >
      <div className="space-y-4">{current.body}</div>
    </SidePanel>
  )
}

function tabLabel(t: ErfassenTab): string {
  if (t === 'entry') return 'Eintrag'
  if (t === 'absence') return 'Abwesenheit'
  return 'Meeting'
}

function TabBar({
  active,
  lockedTab,
  onSelect,
}: {
  active: ErfassenTab
  lockedTab: ErfassenTab | null
  onSelect: (tab: ErfassenTab) => void
}) {
  return (
    <div
      role="tablist"
      aria-label="Erfassen-Tabs"
      className="flex items-center gap-0 border-b border-border-subtle -mx-4 px-4"
    >
      {TABS.map(({ id, label, Icon }) => {
        const isActive = id === active
        const isDisabled = !!lockedTab && id !== lockedTab
        return (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={isActive}
            aria-disabled={isDisabled}
            disabled={isDisabled}
            onClick={() => onSelect(id)}
            title={isDisabled ? 'Im Bearbeiten-Modus nicht wechselbar' : undefined}
            className={cn(
              'inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 -mb-px transition-colors',
              isActive
                ? 'border-accent text-accent'
                : 'border-transparent text-text-secondary hover:text-text-primary',
              isDisabled && 'opacity-40 cursor-not-allowed hover:text-text-secondary',
            )}
          >
            <Icon className="h-3.5 w-3.5" />
            {label}
          </button>
        )
      })}
    </div>
  )
}
